import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Clapperboard, Loader2, RefreshCw, Trash2, Upload, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { inventoryService } from '@/services/inventory';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  createTransferTracker,
  formatBytes,
  formatEta,
  formatRate,
} from './uploadProgress';
import {
  VIDEO_ACCEPTED,
  VIDEO_MAX_BYTES,
  VIDEO_MAX_DURATION_SEC,
  capturePoster,
  formatDuration,
  probeVideo,
  validateVideo,
} from './videoProfile';

interface Props {
  itemId: string | null;
}

/**
 * The item's single storefront product video — one short clip, held apart
 * from the photo gallery so the portal can highlight it on the product page.
 * Upload replaces the previous clip.
 *
 * Every file is measured in the browser before upload (`probeVideo`) and
 * rejected here if it misses the profile, so an oversized or over-long clip
 * never leaves the operator's machine. On a good file we also capture a
 * poster frame and send it along — that still is what makes the storefront
 * free to render `preload="none"`, costing no video bandwidth until a
 * shopper presses play.
 */
export function ItemVideoTab({ itemId }: Props) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [playbackDraft, setPlaybackDraft] = useState<string | null>(null);

  /**
   * Upload has three phases and they need different feedback. "checking" is the
   * local probe + poster capture (seconds, no network). "sending" is the wire
   * transfer, where a percentage and an ETA are possible. "saving" is the part
   * most uploaders get wrong: once the browser has sent the last byte the
   * request is NOT finished — the server is still streaming the file up to S3,
   * and a bar parked at 100% during that reads as a hang. So it becomes an
   * explicit indeterminate state instead.
   */
  const [phase, setPhase] = useState<'idle' | 'checking' | 'sending' | 'saving'>('idle');
  const [sent, setSent] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [eta, setEta] = useState<number | null>(null);
  const [rate, setRate] = useState<number | null>(null);
  const trackerRef = useRef(createTransferTracker());
  const abortRef = useRef<AbortController | null>(null);

  const resetProgress = () => {
    setPhase('idle');
    setSent(0);
    setTotalBytes(0);
    setEta(null);
    setRate(null);
    trackerRef.current.reset();
    abortRef.current = null;
  };

  const videoQuery = useQuery({
    queryKey: ['inventory', 'video', itemId],
    queryFn: () => inventoryService.getVideo(itemId!),
    enabled: !!itemId,
  });

  const upload = useMutation({
    mutationFn: (args: {
      file: File;
      poster: Blob | null;
      meta: { durationSec: number; width: number; height: number };
    }) => {
      const controller = new AbortController();
      abortRef.current = controller;
      trackerRef.current.reset();
      setPhase('sending');
      setSent(0);
      setTotalBytes(args.file.size);
      return inventoryService.uploadVideo(itemId!, args.file, args.poster, args.meta, {
        signal: controller.signal,
        onProgress: (loaded, total) => {
          trackerRef.current.push(loaded);
          setSent(loaded);
          if (total) setTotalBytes(total);
          const target = total || args.file.size;
          setEta(trackerRef.current.etaSeconds(target));
          setRate(trackerRef.current.ratePerSecond());
          // Last byte is out of the browser, but the server still has to put it
          // in storage. Switch to the indeterminate phase rather than sitting
          // at 100%.
          if (loaded >= target) setPhase('saving');
        },
      });
    },
    onSuccess: () => {
      resetProgress();
      qc.invalidateQueries({ queryKey: ['inventory', 'video', itemId] });
      toast.success('Video updated');
    },
    onError: (err: unknown) => {
      const wasCancelled = abortRef.current?.signal.aborted;
      resetProgress();
      if (wasCancelled) {
        toast.info('Upload cancelled');
        return;
      }
      const e = err as { status?: number; message?: string };
      // A 413 here means it cleared our client check but nginx still refused
      // it — worth a distinct message so it isn't mistaken for the profile.
      if (e?.status === 413 || (typeof e?.message === 'string' && /too large|payload/i.test(e.message))) {
        toast.error(
          `That clip is too large to upload. Keep it under ${VIDEO_MAX_BYTES / 1024 / 1024} MB.`,
        );
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    },
  });

  const savePlayback = useMutation({
    mutationFn: (url: string | null) => inventoryService.setVideoPlaybackUrl(itemId!, url),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'video', itemId] });
      setPlaybackDraft(null);
      toast.success('Storefront address saved');
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not save that address'),
  });

  const remove = useMutation({
    mutationFn: () => inventoryService.deleteVideo(itemId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'video', itemId] });
      toast.success('Video removed');
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not remove the video'),
  });

  const handleFile = async (files: FileList | null | undefined) => {
    const file = files?.[0];
    if (!file || !itemId) return;

    setPhase('checking');
    try {
      const probe = await probeVideo(file);
      const problem = validateVideo(file, probe);
      if (problem) {
        toast.error(problem);
        resetProgress();
        return;
      }
      // Poster capture is best-effort — a null poster still uploads fine.
      const poster = await capturePoster(file);
      await upload.mutateAsync({ file, poster, meta: probe });
    } catch (err) {
      // The mutation's own onSuccess/onError already resets progress, so this
      // only covers a failure BEFORE the upload started (unreadable file) or a
      // cancellation, which surfaces its own message.
      if (!abortRef.current?.signal.aborted) {
        toast.error(err instanceof Error ? err.message : 'Could not read that video');
        resetProgress();
      }
    }
  };

  // Add mode — nothing to attach the clip to yet.
  if (!itemId) {
    return (
      <div className="rounded-md border border-dashed bg-muted/30 py-10 text-center">
        <Clapperboard className="mx-auto h-5 w-5 text-muted-foreground/60" />
        <div className="mt-2 text-[13px] font-medium text-foreground">Save the item first</div>
        <div className="mx-auto mt-1 max-w-sm text-[12px] text-muted-foreground">
          Once the item is created you can upload its product video here.
        </div>
      </div>
    );
  }

  const video = videoQuery.data ?? null;
  const busy = upload.isPending || remove.isPending || phase !== 'idle';
  const pct = totalBytes ? Math.min(100, Math.round((sent / totalBytes) * 100)) : 0;

  return (
    <div className="space-y-4">
      <input
        ref={inputRef}
        type="file"
        accept={VIDEO_ACCEPTED.join(',')}
        className="hidden"
        onChange={(e) => {
          void handleFile(e.target.files);
          e.target.value = '';
        }}
      />

      {videoQuery.isLoading ? (
        <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
      ) : video ? (
        <div className="space-y-3">
          <div className="relative overflow-hidden rounded-lg border border-border/70 bg-black">
            {/* `preload="none"` matches how the storefront renders it — the
                poster is all that loads until play is pressed. */}
            <video
              key={video.previewUrl ?? video.id}
              src={video.previewUrl ?? undefined}
              poster={video.posterUrl ?? undefined}
              controls
              preload="none"
              playsInline
              className="aspect-video w-full bg-black object-contain"
            />
            {busy && (
              <div className="absolute inset-0 grid place-items-center bg-background/60">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[12px] text-muted-foreground">
              <span className="font-medium text-foreground">{video.filename}</span>
              {` · ${formatDuration(video.durationSec)}`}
              {video.width && video.height ? ` · ${video.width}×${video.height}` : ''}
              {` · ${fmt.compact(video.size)}B`}
              {video.posterUrl ? '' : ' · no poster'}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
              >
                <RefreshCw className="h-3.5 w-3.5" /> Replace
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => remove.mutate()}
                disabled={busy}
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </Button>
            </div>
          </div>

          {/* Where the shop actually plays it from. ZAFROI stores the master
              but never serves video to shoppers — one play is 25-100 MB of
              egress, which at storefront volumes is the entire hosting bill.
              The shop mirrors the clip to its own CDN and that address goes
              here. Until it does, the product page shows the poster only. */}
          <div className="rounded-md border border-border/70 bg-muted/30 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[13px] font-semibold text-foreground">
                Storefront address
              </span>
              {video.playbackUrl ? (
                <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                  Live on the shop
                </span>
              ) : (
                <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-600 dark:text-amber-500">
                  Not on the shop yet
                </span>
              )}
            </div>
            <p className="mt-1.5 text-[11.5px] leading-relaxed text-muted-foreground">
              The shop plays this clip from its own CDN, not from ZAFROI. Paste the address it
              is served from once it has been copied across. Until then the product page shows
              the still image with no player.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                type="url"
                inputMode="url"
                placeholder="https://cdn.yourshop.com/videos/…"
                className="h-9 min-w-[260px] flex-1 rounded-md border border-input bg-background px-3 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={playbackDraft ?? video.playbackUrl ?? ''}
                onChange={(e) => setPlaybackDraft(e.target.value)}
                disabled={savePlayback.isPending}
              />
              <Button
                size="sm"
                onClick={() => savePlayback.mutate(playbackDraft?.trim() ? playbackDraft.trim() : null)}
                disabled={savePlayback.isPending || playbackDraft === null}
              >
                {savePlayback.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          </div>
        </div>
      ) : phase !== 'idle' ? (
        /* Upload in flight — a bare spinner on a multi-minute transfer is
           indistinguishable from a hang, which is what made this feel broken. */
        <div className="rounded-lg border border-border/70 bg-muted/30 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[13px] font-semibold text-foreground">
                {phase === 'checking' && 'Checking the clip…'}
                {phase === 'sending' && 'Uploading video'}
                {phase === 'saving' && 'Saving to storage…'}
              </div>
              <div className="mt-0.5 text-[11.5px] text-muted-foreground">
                {phase === 'checking' && 'Reading length and size, and taking the cover frame.'}
                {phase === 'sending' && (
                  <>
                    {formatBytes(sent)} of {formatBytes(totalBytes)}
                    {formatRate(rate) ? ` · ${formatRate(rate)}` : ''} · {formatEta(eta)}
                  </>
                )}
                {phase === 'saving' && 'Upload finished — putting it away. Almost there.'}
              </div>
            </div>
            {phase === 'sending' && (
              <Button
                size="sm"
                variant="ghost"
                className="shrink-0 text-muted-foreground"
                onClick={() => abortRef.current?.abort()}
              >
                <X className="h-3.5 w-3.5" /> Cancel
              </Button>
            )}
          </div>

          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-border/70">
            {phase === 'sending' ? (
              <div
                className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
                style={{ width: `${pct}%` }}
              />
            ) : (
              /* Indeterminate: we genuinely cannot know how far along the
                 server is, and a fake percentage would be a lie. */
              <div className="h-full w-1/3 animate-pulse rounded-full bg-primary/70" />
            )}
          </div>

          {phase === 'sending' && (
            <div className="mt-1.5 text-right text-[11px] font-medium tabular-nums text-muted-foreground">
              {pct}%
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            void handleFile(e.dataTransfer.files);
          }}
          disabled={busy}
          className={cn(
            'flex aspect-video w-full flex-col items-center justify-center rounded-lg border-2 border-dashed text-center transition-colors',
            dragOver
              ? 'border-primary bg-primary/5'
              : 'border-border bg-muted/30 hover:border-primary/50',
          )}
        >
          <Upload className="h-6 w-6 text-muted-foreground" />
          <div className="mt-2 text-[13px] text-foreground">
            Drag a video here, or <span className="font-medium text-primary">choose a file</span>
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            MP4 (H.264) · up to {VIDEO_MAX_DURATION_SEC}s · max 1080p ·{' '}
            {VIDEO_MAX_BYTES / 1024 / 1024} MB
          </div>
        </button>
      )}

      <p className="text-[11.5px] leading-relaxed text-muted-foreground">
        One product video per item, shown separately from the photo gallery on the storefront
        product page. A still is captured automatically and used as the poster, so the shop loads
        only that image until a shopper presses play. The preview above uses a temporary link that
        expires — the shop serves the clip from its own address, never from ZAFROI.
      </p>
    </div>
  );
}
